import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setIsAiEnabled } from "../../redux/slices/aiSlice";
import Apiurl from "../../Helpers/apiUrl";
import { Link, useLocation, Outlet } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { CgMinimize } from "react-icons/cg";
import HomeIcon from "@mui/icons-material/Home";
import GridViewIcon from "@mui/icons-material/GridView";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import EditNoteSharpIcon from "@mui/icons-material/EditNoteSharp";
import SummarizeIcon from "@mui/icons-material/Summarize";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SettingsIcon from "@mui/icons-material/Settings";
import { Inventory2 } from "@mui/icons-material";
import bgVideo from "../../../src/bg-video/Grohe_New_KV_Option-1.mp4";
import { userModuleVersion } from "../../Helpers/appConfig";

/*
 * Sidebar widths are the ORIGINAL ones:
 *   collapsed = w-[5rem] (80px)   expanded = w-52 (208px)
 * Nav padding is px-4 (16px), so the collapsed inner column is exactly 48px
 * and every icon sits dead-centre. Content margins (ml-20 / ml-52) match.
 */

const UserLayout = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [activeLink, setActiveLink] = useState("/user");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [inventoryVisible, setInventoryVisible] = useState(false);

  const _code_sign = localStorage.getItem("_code_sign");
  const userName = localStorage.getItem("user_name") || "User";

  // `group` is only used to draw a thin divider between sets of tabs
  const navLinks = [
    { path: "/user", label: "Home", icon: HomeIcon, group: 1 },
    ...(_code_sign !== "LIXIL/5087"
      ? [{ path: "/user/dashboard", label: "Dashboard", icon: GridViewIcon, group: 1 }]
      : []),
    { path: "/user/viewclient", label: "Add / View Client", icon: PeopleAltIcon, group: 2 },
    // { path: "/user/create-quote", label: "Create Quotation", icon: DescriptionIcon, useAnchor: true, group: 2 },
    { path: "/user/create-quote-fx", label: "Create Quotation", icon: DescriptionIcon, useAnchor: true, group: 2 }, 
    { path: "/user/draft", label: "Draft", icon: EditNoteSharpIcon, group: 2 },
    { path: "/user/summary", label: "Summary", icon: SummarizeIcon, group: 2 },
    { path: "/user/manual", label: "Manual", icon: MenuBookIcon, group: 3 },
    ...(inventoryVisible
      ? [{ path: "/user/inventory", label: "Inventory", icon: Inventory2, group: 3 }]
      : []),
    { path: "/user/pdf-settings", label: "PDF Settings", icon: PictureAsPdfIcon, group: 4 },
    { path: "/user/settings", label: "Settings", icon: SettingsIcon, group: 4 },
    { path: "/user/help-desk", label: "Helpdesk", icon: HelpCenterIcon, group: 4 },
  ];

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    fetch(`${Apiurl}system-settings`, { cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setInventoryVisible(data.inventory_menu === 1);
          dispatch(setIsAiEnabled(data.ask_ai === 1 || data.ask_ai === "1"));
        }
      })
      .catch((err) => console.error("Error fetching system settings:", err));
  }, [dispatch]);

  const isHome = location.pathname === "/user";

  return (
    <>
      {/* Background video */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <video
          src={bgVideo}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-center"
        />
      </div>

      <div className="relative z-10 flex w-full overflow-x-hidden">
        {/* ───────────── Sidebar ───────────── */}
        <aside className={`fixed inset-y-0 left-0 z-50 ${isHome ? "hidden" : "block"}`}>
          <nav
            aria-label="Main navigation"
            onMouseEnter={() => setIsSidebarOpen(true)}
            onMouseLeave={() => setIsSidebarOpen(false)}
            className={`flex h-screen flex-col overflow-hidden border-r border-white/10 bg-gray-900 text-white shadow-2xl
              transition-[width] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
              ${isSidebarOpen ? "w-52" : "w-[5rem]"}`}
          >
            {/* Header — h-16 to line up with the top bar */}
            <div className="flex h-16 shrink-0 items-center border-b border-white/10 px-4">
              <div className="flex w-12 shrink-0 items-center justify-center">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#ff7b00]/15 text-xs font-bold uppercase text-[#ff7b00] ring-1 ring-[#ff7b00]/40">
                  {userName.charAt(0)}
                </div>
              </div>

              <div
                className={`flex min-w-0 flex-1 items-center justify-between pl-2 transition-opacity duration-300 ${
                  isSidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
              >
                <div className="min-w-0 leading-tight">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
                    Welcome
                  </p>
                  <p className="truncate text-xs font-semibold text-white">{userName}</p>
                </div>
                <button
                  type="button"
                  aria-label="Collapse sidebar"
                  onClick={() => setIsSidebarOpen(false)}
                  className="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <CgMinimize size={14} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <ul className="min-h-0 flex-1 overflow-hidden px-4 py-3">
              {navLinks.map((link, index) => {
                const Icon = link.icon;
                const isActive =
                  link.path === "/user"
                    ? activeLink === "/user"
                    : activeLink.startsWith(link.path);
                const shouldShowAnchor =
                  link.useAnchor && activeLink.startsWith("/user/projects/");
                const startsNewGroup = index > 0 && navLinks[index - 1].group !== link.group;

                const itemClasses = `group relative flex h-10 items-center rounded-lg [@media(max-height:700px)]:h-9 transition-all duration-200 ${
                  isActive
                    ? "bg-white/10 text-white ring-1 ring-inset ring-[#ff7b00]"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`;

                const content = (
                  <>
                    <span className="flex w-12 shrink-0 items-center justify-center">
                      <Icon
                        style={{ fontSize: 20 }}
                        className={`transition-transform duration-200 group-hover:scale-110 ${
                          isActive ? "!text-[#ff7b00]" : ""
                        }`}
                      />
                    </span>
                    <span
                      className={`whitespace-nowrap text-[13px] tracking-wide transition-opacity duration-200 ${
                        isActive ? "font-semibold" : "font-medium"
                      } ${isSidebarOpen ? "opacity-100" : "opacity-0"}`}
                    >
                      {link.label}
                    </span> 
                  </>
                );

                const item = shouldShowAnchor ? (
                  <a
                    href={link.path}
                    className={itemClasses}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => setActiveLink(link.path)}
                  >
                    {content}
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    className={itemClasses}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => setActiveLink(link.path)}
                  >
                    {content}
                  </Link>
                );

                return (
                  <li key={link.path} className={startsNewGroup ? "mt-2 border-t border-white/10 pt-2" : "mt-0.5 first:mt-0"}>
                    <Tooltip title={isSidebarOpen ? "" : link.label} placement="right" arrow>
                      {item}
                    </Tooltip>
                  </li>
                );
              })}
            </ul>

            {/* Footer */}
            <div className="shrink-0 border-t border-white/10 px-4 py-2.5 text-center">
              {isSidebarOpen ? (
                <div className="space-y-0.5 leading-tight">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">
                    Version {userModuleVersion}
                  </p>
                  <p className="text-[9px] text-gray-500">
                    Made with <FontAwesomeIcon icon={faHeart} className="text-red-700" /> by Ruminate IT Solution
                  </p>
                </div>
              ) : (
                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-500">
                  V-{userModuleVersion}
                </p>
              )}
            </div>
          </nav>
        </aside>

        {/* ───────────── Page content (original margins) ───────────── */}
        <main
          className={`min-w-0 flex-1 overflow-x-hidden transition-[margin] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
            isHome ? "ml-0" : `mt-16 p-1 ${isSidebarOpen ? "ml-52" : "ml-20"}`
          }`}
        >
          <Outlet context={{ isSidebarOpen }} />
        </main>
      </div>
    </>
  );
};

export default UserLayout;